import { useState } from 'react';
import styled from 'styled-components';
import { Button, EmptyState, Input, StatusPill } from './ui';
import { sessionTitle } from '../lib/messages';
import type { OpencodeStatus, Session } from '../types';

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 4px 12px;

  h2 {
    margin: 0;
    color: ${p => p.theme.text};
    font-family: 'Raleway', system-ui, sans-serif;
    font-size: 16px;
    font-weight: 900;
  }
`;

const CreateRow = styled.form`
  display: flex;
  gap: 8px;
  padding: 0 4px 12px;

  ${Button} {
    padding: 12px 14px;
    border-radius: 10px;
  }

  input {
    border-radius: 10px;
  }

  @media (max-width: 520px) { flex-direction: column; }
`;

const List = styled.div`
  padding: 0 4px;
`;

const Item = styled.div<{ $selected?: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin: 8px 0;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid ${p => (p.$selected ? p.theme.primary : p.theme.border)};
  background: ${p => (p.$selected ? p.theme.primarySoft : p.theme.surfaceStrong)};
  color: ${p => p.theme.text};
  cursor: ${p => (p.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${p => (p.$disabled ? 0.6 : 1)};
  transition: background .15s ease, border-color .15s ease, transform .15s ease;

  &:hover {
    border-color: ${p => p.theme.primary};
    background: ${p => p.theme.primarySoft};
    transform: ${p => (p.$disabled ? 'none' : 'translateY(-1px)')};
  }
`;

const ChildItem = styled(Item)`
  margin-left: 18px;
  border-left: 3px solid ${p => p.theme.warning};
`;

const TextBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

const SessionTitle = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 600;
`;

const ContextLine = styled.div`
  margin-top: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${p => p.theme.muted};
  font-size: 12px;
  font-weight: 600;
`;

const DeleteMini = styled.button`
  flex: 0 0 auto;
  border: 1px solid ${p => p.theme.border};
  border-radius: 9px;
  background: ${p => p.theme.surface};
  color: ${p => p.theme.muted};
  padding: 6px 8px;
  font-size: 12px;
  font-weight: 700;

  &:hover {
    color: ${p => p.theme.dangerStrong};
    background: ${p => p.theme.dangerSoft};
  }
`;

const SidebarEmpty = styled(EmptyState)`
  min-height: 180px;
  background: ${p => p.theme.surfaceSoft};
  border-color: ${p => p.theme.border};
  color: ${p => p.theme.muted};
`;

type Props = {
  sessions: Session[];
  selectedSession: Session | null;
  status: OpencodeStatus;
  onCreateSession: (name: string) => void;
  onDeleteSession: (id: string) => void;
  onSelectSession: (session: Session) => void;
};

export function SessionSidebar({ sessions, selectedSession, status, onCreateSession, onDeleteSession, onSelectSession }: Props) {
  const [name, setName] = useState('');
  const parents = sessions.filter(session => !session.parentID);
  const childrenByParent = new Map<string, Session[]>();

  sessions.forEach(session => {
    if (!session.parentID) return;
    if (!childrenByParent.has(session.parentID)) childrenByParent.set(session.parentID, []);
    childrenByParent.get(session.parentID)?.push(session);
  });

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const value = name.trim();
    if (!value) return;
    onCreateSession(value);
    setName('');
  }

  function contextLine(session: Session): string {
    const parts = [
      session.context?.company?.name,
      session.context?.workspace?.name,
      session.context?.user?.email,
    ].filter(Boolean);
    return parts.join(' · ');
  }

  return (
    <>
      <Header>
        <h2>Sessions</h2>
        <StatusPill $ok={Boolean(status.ok)}>{status.ok ? 'Live' : 'Check'}</StatusPill>
      </Header>
      <CreateRow onSubmit={submit}>
        <Input value={name} onChange={event => setName(event.target.value)} placeholder="Session name" />
        <Button type="submit">Create</Button>
      </CreateRow>
      <List>
        {!parents.length && <SidebarEmpty>No sessions yet. Create one and get moving.</SidebarEmpty>}
        {parents.map(parent => {
          const children = childrenByParent.get(parent.id) || [];
          return (
            <div key={parent.id}>
              <Item $selected={selectedSession?.id === parent.id} onClick={() => onSelectSession(parent)}>
                <TextBlock>
                  <SessionTitle title={sessionTitle(parent)}>{sessionTitle(parent)}</SessionTitle>
                  {contextLine(parent) && <ContextLine title={contextLine(parent)}>{contextLine(parent)}</ContextLine>}
                </TextBlock>
                <DeleteMini onClick={event => { event.stopPropagation(); onDeleteSession(parent.id); }}>Delete</DeleteMini>
              </Item>
              {children.map(child => (
                <ChildItem key={child.id} $selected={selectedSession?.id === child.id} $disabled onClick={() => onSelectSession(child)}>
                  <TextBlock>
                    <SessionTitle title={`${sessionTitle(child)} (read-only)`}>↳ {sessionTitle(child)}</SessionTitle>
                    {contextLine(child) && <ContextLine title={contextLine(child)}>{contextLine(child)}</ContextLine>}
                  </TextBlock>
                  <DeleteMini onClick={event => { event.stopPropagation(); onDeleteSession(child.id); }}>Delete</DeleteMini>
                </ChildItem>
              ))}
            </div>
          );
        })}
      </List>
    </>
  );
}
